import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';

import diagramIcon from './images/diagram.svg';
import gridIcon from './images/grid.svg';
import monitorIcon from './images/monitor.svg';
import multiWindowIcon from './images/multi-window.svg';
import personIcon from './images/person.svg';
import stopwatchIcon from './images/stopwatch.svg';

const icons = {
  diagram: {
    icon: diagramIcon,
    shadow: 'shadow-[0_6px_20px_rgba(230,233,248,0.70),0_2px_8px_rgba(92,112,214,0.20)]',
  },
  grid: {
    icon: gridIcon,
    shadow: 'shadow-[0_6px_20px_rgba(219,240,231,0.60),0_2px_8px_rgba(76,178,136,0.16)]',
  },
  monitor: {
    icon: monitorIcon,
    shadow: 'shadow-[0_6px_20px_rgba(219,240,231,0.60),0_2px_8px_rgba(76,178,136,0.16)]',
  },
  multiWindow: {
    icon: multiWindowIcon,
    shadow: 'shadow-[0_6px_20px_rgba(248,230,230,0.60),0_2px_8px_rgba(191,64,64,0.12)]',
  },
  person: {
    icon: personIcon,
    shadow: 'shadow-[0_6px_20px_rgba(248,230,230,0.60),0_2px_8px_rgba(191,64,64,0.12)]',
  },
  stopwatch: {
    icon: stopwatchIcon,
    shadow: 'shadow-[0_6px_20px_rgba(230,233,248,0.70),0_2px_8px_rgba(92,112,214,0.20)]',
  },
};

const Advantages = ({ className, title, description, items }) => (
  <section className={clsx('safe-paddings bg-new-gray-2', className)}>
    <div className="container py-[120px] xl:py-20 md:py-14">
      <h2 className="text-center text-[56px] font-medium leading-denser tracking-tight-4 text-new-black xl:text-6xl lg:text-left">
        {title}
      </h2>
      <p className="max-w-xl mt-5 mx-auto font-light text-xl leading-snug text-center text-new-gray-1 lg:max-w-max lg:mx-0 lg:text-left md:mt-2">
        {description}
      </p>
      <ul className="grid grid-cols-3 gap-y-[57px] gap-x-[120px] mt-20 xl:mt-14 xl:gap-y-10 lg:mt-10 lg:grid-cols-2 lg:gap-y-10 md:mt-8 md:block md:space-y-8">
        {items.map(({ iconName, title, description }, index) => (
          <li
            className={clsx('col-span-1', {
              'lg:order-1': index === 0,
              'lg:order-2': index === 1,
              'lg:order-3': index === 3,
              'lg:order-4': index === 2,
              'lg:order-5': index === 4,
              'lg:order-6': index === 5,
            })}
            key={index}
          >
            {icons[iconName].icon && (
              <span
                className={clsx(
                  'flex justify-center items-center size-[60px] bg-white rounded-full xl:size-14 lg:size-12',
                  icons[iconName].shadow
                )}
              >
                <img
                  className="lg:size-5"
                  src={icons[iconName].icon}
                  alt=""
                  width="28"
                  height="28"
                  loading="lazy"
                />
              </span>
            )}
            <h3 className="mt-7 text-2xl leading-none tracking-tight-4 md:mt-3.5 text-new-black">
              {title}
            </h3>
            <p className="mt-3 font-light text-base leading-normal lg:max-w-[285px] md:mt-1.5 md:max-w-none text-new-gray-1">
              {description}
            </p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

Advantages.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      iconName: PropTypes.oneOf(Object.keys(icons)).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ).isRequired,
};

Advantages.defaultProps = {
  className: null,
};

export default Advantages;
